import {
  DEFAULT_JOURNEY_FUNNEL_PERIOD,
  type JourneyFunnelPeriod,
  type JourneyFunnelStepKey,
  type JourneyStepPatients,
  type OnboardingCohortReport,
  type OnboardingFunnelReport,
  type PatientGroup,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { commonHeaders, jsonOr401 } from "./clinician-api";

// TES-1076 — the onboarding funnel client (admin-only endpoint).

export class OnboardingFunnelApiError extends Error {}

/** `from`/`to` are inclusive Stockholm days, YYYY-MM-DD. */
export async function fetchOnboardingFunnel(
  from: string,
  to: string,
): Promise<OnboardingFunnelReport> {
  const qs = new URLSearchParams({ from, to });
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/onboarding-funnel?${qs}`,
    {
      headers: tenantHeader(),
      ...withCredentials,
    },
  );
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<OnboardingFunnelReport>(res);
  }
  if (!res.ok) throw new OnboardingFunnelApiError(`HTTP ${res.status}`);
  return (await res.json()) as OnboardingFunnelReport;
}

/**
 * TES-1096 — the cohort whose start falls in `from`–`to`. TES-1117 S3 —
 * `journey` (null = Alla) keeps one journey and adds its own funnel, over
 * the people whose journey started in `period` (Ö-354).
 */
export async function fetchOnboardingCohort(
  from: string,
  to: string,
  journey: PatientGroup | null = null,
  period: JourneyFunnelPeriod = DEFAULT_JOURNEY_FUNNEL_PERIOD,
): Promise<OnboardingCohortReport> {
  const qs = new URLSearchParams({ from, to });
  if (journey) {
    qs.set("journey", journey);
    qs.set("period", period);
  }
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/onboarding-funnel/cohort?${qs}`,
    {
      headers: tenantHeader(),
      ...withCredentials,
    },
  );
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<OnboardingCohortReport>(res);
  }
  if (!res.ok) throw new OnboardingFunnelApiError(`HTTP ${res.status}`);
  return (await res.json()) as OnboardingCohortReport;
}

/**
 * TES-1117 S3 — the people standing at one step of a journey's funnel.
 * Admin only and identified: the API logs every patient it returns as a
 * read on the support basis (PDL purpose: administration), as for the
 * TES-1109 contact list.
 */
export async function fetchJourneyStepPatients(
  period: JourneyFunnelPeriod,
  journey: PatientGroup,
  step: JourneyFunnelStepKey,
): Promise<JourneyStepPatients> {
  const qs = new URLSearchParams({ period, journey, step });
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/onboarding-funnel/journey-patients?${qs}`,
    {
      headers: { ...commonHeaders(), "X-Access-Purpose": "administration" },
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}
